import { kitColourHex, localAgeLabel, squadAgeInfo } from '../utils/teamFixtures';
import { useT } from '../contexts/LocaleContext';
import { buildFixtureFinderText } from '../utils/fixtureFinder';

// Read-only card for one squad from the Team Info form: age group, kits and
// preferred fixture. Shared by the tour leader and football operations portals.

const JERSEY_PATH = 'M 8,8 L 22,18 Q 36,8 50,12 Q 64,8 78,18 L 92,8 L 100,30 L 82,42 L 78,110 L 22,110 L 18,42 L 0,30 Z';

function KitModel({ jerseyColour, jerseyColour2, jerseyPattern, shortsColour, socksColour, uid }) {
  const c1  = kitColourHex(jerseyColour)  || '#E5E7EB';
  const c2  = kitColourHex(jerseyColour2) || '#FFFFFF';
  const sc  = kitColourHex(shortsColour)  || '#E5E7EB';
  const sk  = kitColourHex(socksColour)   || '#E5E7EB';
  const pat = jerseyPattern || 'plain';
  const gradId  = `kg-${uid}`;
  const stripId = `ks-${uid}`;
  const hoopId  = `kh-${uid}`;
  return (
    <svg viewBox="0 0 100 220" width="80" height="176" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="50%" stopColor={c1}/><stop offset="50%" stopColor={c2}/>
        </linearGradient>
        <pattern id={stripId} x="0" y="0" width="16" height="110" patternUnits="userSpaceOnUse">
          <rect width="8"  height="110" fill={c1}/><rect x="8" width="8" height="110" fill={c2}/>
        </pattern>
        <pattern id={hoopId} x="0" y="0" width="100" height="20" patternUnits="userSpaceOnUse">
          <rect width="100" height="10" fill={c1}/><rect y="10" width="100" height="10" fill={c2}/>
        </pattern>
      </defs>
      <path d={JERSEY_PATH}
        fill={pat === 'halves' ? `url(#${gradId})` : pat === 'stripes' ? `url(#${stripId})` : pat === 'hoops' ? `url(#${hoopId})` : c1}
        stroke="#fff" strokeWidth="1.5"/>
      <rect x="22" y="110" width="56" height="60" rx="4" fill={sc} stroke="#fff" strokeWidth="1.5"/>
      <rect x="22" y="170" width="26" height="28" rx="3" fill={sk} stroke="#fff" strokeWidth="1.5"/>
      <rect x="52" y="170" width="26" height="28" rx="3" fill={sk} stroke="#fff" strokeWidth="1.5"/>
    </svg>
  );
}

export default function SquadCard({ squad: sq, index: i, request, uidPrefix = 'kit', showFinder = true }) {
  const t = useT();
  const age = squadAgeInfo(sq, request);
  return (
    <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
      <div className="flex items-center justify-between px-[18px] py-[12px] border-b border-[#F1F3F6] bg-surface">
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-[#111827]">{sq.label || t('squad.squadN', { n: i + 1 })}</p>
          {age.label && (
            <p className="text-[11.5px] text-[#6B7280]">{localAgeLabel(age.label, t)}</p>
          )}
        </div>
        {/* Fixture finder (staff tool — not shown to suppliers) */}
        {showFinder && <button onClick={() => { navigator.clipboard.writeText(buildFixtureFinderText(sq, request)); alert(t('squad.copied')); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-[8px] hover:text-pb hover:border-pb transition">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
          {t('squad.copyFinder')}
        </button>}
      </div>
      <div className="px-[18px] py-[16px]">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          {[[t('squad.gender'), t.value('gender', sq.gender)], [t('squad.yearsOfBirth'), Array.isArray(sq.yearsOfBirth) && sq.yearsOfBirth.length ? sq.yearsOfBirth.join(', ') : sq.yearOfBirth], [t('squad.playersPerMatch'), sq.playersPerMatch], [t('squad.ability'), t.value('ability', sq.abilityLevel)]].map(([label, val]) => (
            <div key={label}>
              <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[3px]">{label}</p>
              <p className="text-[13px] font-semibold text-[#111827]">{val || '—'}</p>
            </div>
          ))}
        </div>

        {/* Kits */}
        <div className="space-y-5">
          {[
            { title: t('squad.homeKit'), jersey: sq.homeJerseyColour, jersey2: sq.homeJerseyColour2, pattern: sq.homeJerseyPattern, shorts: sq.homeShortsColour, socks: sq.homeSocksColour },
            ...(!sq.onlyOneKit ? [{ title: t('squad.awayKit'), jersey: sq.awayJerseyColour, jersey2: sq.awayJerseyColour2, pattern: sq.awayJerseyPattern, shorts: sq.awayShortsColour, socks: sq.awaySocksColour }] : []),
          ].map(({ title, jersey, jersey2, pattern, shorts, socks }, kitIdx) => (
            <div key={title}>
              <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-3">{title}</p>
              <div className="flex items-start gap-5">
                <KitModel uid={`${uidPrefix}-${i}-${kitIdx}`} jerseyColour={jersey} jerseyColour2={jersey2} jerseyPattern={pattern} shortsColour={shorts} socksColour={socks} />
                <div className="flex flex-wrap gap-4 items-end pt-1">
                  {[[t('squad.jersey'), jersey], ...(jersey2 ? [[t('squad.jersey2'), jersey2]] : []), [t('squad.shorts'), shorts], [t('squad.socks'), socks]].map(([label, val]) => (
                    <div key={label} className="text-center">
                      <div className="w-9 h-9 rounded-[8px] border-2 border-[#E5E7EB] mb-1" style={{ background: kitColourHex(val) || '#F3F4F6' }} />
                      <p className="text-[11px] text-[#6B7280]">{label}</p>
                      <p className="text-[11px] font-semibold text-[#111827]">{t.value('colour', val) || '—'}</p>
                    </div>
                  ))}
                  {pattern && pattern !== 'plain' && <p className="text-[12px] text-[#6B7280] self-end mb-1">{t('squad.pattern')} <span className="font-semibold capitalize">{t.value('pattern', pattern)}</span></p>}
                </div>
              </div>
            </div>
          ))}
          {sq.onlyOneKit && <p className="text-[12px] text-[#9CA3AF]">{t('squad.oneKitOnly')}</p>}
        </div>

        {/* Preferred fixture */}
        {sq.preferredFixture && (sq.preferredFixture.preferredDate || sq.preferredFixture.preferredTime || sq.preferredFixture.preferredLocation) && (
          <div className="mt-4 pt-4 border-t border-[#F1F3F6]">
            <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-2">{t('squad.preferredFixture')}</p>
            <div className="flex flex-wrap gap-4">
              {sq.preferredFixture.preferredDate && <div><p className="text-[10px] text-[#9CA3AF] uppercase font-bold tracking-wide mb-0.5">{t('squad.date')}</p><p className="text-[13px] font-semibold text-[#111827]">{new Date(sq.preferredFixture.preferredDate + 'T00:00:00').toLocaleDateString(t.dateLocale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p></div>}
              {sq.preferredFixture.preferredTime && <div><p className="text-[10px] text-[#9CA3AF] uppercase font-bold tracking-wide mb-0.5">{t('squad.time')}</p><p className="text-[13px] font-semibold text-[#111827]">{t('squad.ko', { time: sq.preferredFixture.preferredTime })}</p></div>}
              {sq.preferredFixture.preferredLocation && <div><p className="text-[10px] text-[#9CA3AF] uppercase font-bold tracking-wide mb-0.5">{t('squad.location')}</p><p className="text-[13px] font-semibold text-[#111827]">{sq.preferredFixture.preferredLocation}</p></div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
